import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBillingStore } from '@/stores/billingStore';
import Icon from '../components/Icon';

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const { checkoutData, isLoading, error, initiateCheckout, clearCheckout } = useBillingStore();
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [widgetError, setWidgetError] = useState<string | null>(null);

  // Initiate checkout on mount
  useEffect(() => {
    initiateCheckout();
    return () => {
      clearCheckout();
    };
  }, [initiateCheckout, clearCheckout]);

  // Load SIBS widget script when checkout data is available
  useEffect(() => {
    if (!checkoutData?.formContext || !checkoutData?.widgetUrl) return;
    if (!formRef.current) return;

    // SIBS SPG widget configuration
    const spgConfig = JSON.stringify({
      paymentMethodList: ['CARD', 'MBWAY', 'REFERENCE'],
      amount: {
        value: checkoutData.amount,
        currency: checkoutData.currency,
      },
      language: 'pt',
      redirectUrl: `${window.location.origin}/billing/checkout/callback?paymentId=${checkoutData.paymentId}`,
    });

    // SIBS SPG widget styling — match app theme
    const spgStyle = JSON.stringify({
      transaction: {
        layout: 'default',
        theme: 'default',
        color: {
          primary: '#3b82f6',
          secondary: '#64748b',
          border: '#e2e8f0',
          surface: '#ffffff',
          header: {
            text: '#1e293b',
            background: '#f8fafc',
          },
          body: {
            text: '#334155',
            background: '#ffffff',
          },
        },
      },
      font: 'Inter, system-ui, sans-serif',
    });

    formRef.current.setAttribute('spg-context', checkoutData.formContext);
    formRef.current.setAttribute('spg-config', spgConfig);
    formRef.current.setAttribute('spg-style', spgStyle);

    // Clean up previous script if any
    if (scriptRef.current) {
      scriptRef.current.remove();
      scriptRef.current = null;
    }

    const script = document.createElement('script');
    script.src = checkoutData.widgetUrl;
    script.async = true;
    script.onload = () => {
      setWidgetLoaded(true);
    };
    script.onerror = () => {
      setWidgetError('Failed to load payment widget');
    };

    document.body.appendChild(script);
    scriptRef.current = script;

    return () => {
      if (scriptRef.current) {
        scriptRef.current.remove();
        scriptRef.current = null;
      }
    };
  }, [checkoutData]);

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Preparing checkout...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6 text-center">
          <Icon name="shopping_cart" size={36} className="text-amber-500 mb-3 block" />
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">No items in cart</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-4">Please select a plan first to proceed with checkout.</p>
          <button
            onClick={() => navigate('/billing/plans')}
            className="px-5 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
          >
            View Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/billing')}
          className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
        >
          <Icon name="arrow_back" size={16} />
          Back to Billing
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
        {/* Order Summary */}
        {checkoutData && (
          <div className="p-6 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-200 mb-4">Checkout</h1>
            <div className="flex items-center justify-between">
              <span className="text-slate-600 dark:text-slate-400">Total</span>
              <span className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                {checkoutData.amount.toFixed(2)} {checkoutData.currency}
              </span>
            </div>
          </div>
        )}

        {/* SIBS Payment Widget */}
        <div className="p-6">
          {widgetError ? (
            <div className="text-center py-8">
              <Icon name="warning" size={30} className="text-amber-500 mb-2 block" />
              <p className="text-slate-600 dark:text-slate-400">{widgetError}</p>
            </div>
          ) : !widgetLoaded && checkoutData ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3"></div>
              <span className="text-slate-600 dark:text-slate-400">Loading payment form...</span>
            </div>
          ) : null}

          {/* SIBS SPG widget — auto-initializes via class + spg-context + spg-config + spg-style */}
          <form ref={formRef} className="paymentSPG" />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-400 dark:text-slate-500">
        <Icon name="lock" size={16} />
        <span>Payments processed securely by SIBS</span>
      </div>
    </div>
  );
};
